import removeAttachmentPreviewsForRow from './removeAttachmentPreviewsForRow';
import getDedupedInlinePreviews from '../helpers/getDedupedInlinePreviews';
import { logVerboseUsage } from 'owa-analytics';
import { lazyShouldShowImageView } from 'owa-attachment-model-store';
import { lazyCreateAttachmentFullViewStateAndStoreBackingModel } from 'owa-attachment-well-data';
import { ClientItemId, ClientAttachmentId } from 'owa-client-ids';
import { MailboxInfo } from 'owa-client-ids';
import AttachmentPreviewWellView from 'owa-mail-list-store/lib/store/schema/AttachmentPreviewWellView';
import ListViewStore from 'owa-mail-list-store/lib/store/schema/ListViewStore';
import listViewStore from 'owa-mail-list-store/lib/store/Store';
import { mailStore } from 'owa-mail-store';
import AttachmentPreview from 'owa-service/lib/contract/AttachmentPreview';
import AttachmentType from 'owa-service/lib/contract/AttachmentType';
import ConversationType from 'owa-service/lib/contract/ConversationType';
import { action, select } from 'satcheljs/lib/legacy';
import { lazyGetAttachment } from 'owa-attachment-model-store';
// tslint:disable-next-line:forbid-import
import { observable } from 'mobx';

export interface ProcessAttachmentState {
    listView: ListViewStore;
}

// CTQ marker when getAttachmentPreviews returned no previews for a conversation
const INLINEPREVIEWS_FALSEPOSITIVE: string = 'IP_FP';
const INLINEPREVIEWS_INLINEIMAGE_DUP: string = 'IP_II_DP';

/**
 * Creates and returns the key for a attachment view state for a particular attachment on a row
 * @param rowId  id of the row
 * @param attachmentId id of the attachment
 * @return - key to store the attachmentViewState in the attachmentViewState map
 */
function getRowAttachmentViewStateKey(rowId: string, attachmentId: string): string {
    return rowId + '_' + attachmentId;
}

export let createAndStoreAttachmentViewState = action('createAndStoreAttachmentViewState')(
    async function createAndStoreAttachmentViewState(
        rowId: string,
        parentItemId: ClientItemId,
        attachment: AttachmentType,
        attachmentPreviewWellView: AttachmentPreviewWellView,
        mailboxInfo: MailboxInfo
    ) {
        const attachmentViewStateKey = getRowAttachmentViewStateKey(
            rowId,
            attachment.AttachmentId.Id
        );
        let attachmentViewState = listViewStore.attachmentViewStates.get(attachmentViewStateKey);

        // Pre-create the client attachment ID as an observable so we can use it throughout this function
        // Get rid of observable.object (bug #38655)
        const attachmentId: ClientAttachmentId = observable.object({
            ...attachment.AttachmentId,
            mailboxInfo: mailboxInfo,
        });

        /**
         * Because we are calling the lazy function we set the attachmentViewState in the promise.
         * This guarantees that the order of the attachments is also preserved. If the attachmentViewState is present
         * in the store and if we directly push it and then push the ones that are not present after they are lazily created
         * there are chances that we go out of order.
         */
        await lazyCreateAttachmentFullViewStateAndStoreBackingModel
            .import()
            .then(async createAttachmentFullViewStateAndStoreBackingModel => {
                attachmentViewState =
                    attachmentViewState ||
                    createAttachmentFullViewStateAndStoreBackingModel(
                        attachmentId,
                        attachment,
                        true /* isReadOnly */,
                        true /* uploadCompleted */,
                        false /* forceStoreBackingModel */,
                        false /* isSMIMEItem */,
                        parentItemId
                    );

                // Use the attachment model from the store
                attachment = (await lazyGetAttachment.import())(attachmentId).model;

                // Set the attachmentViewState in the store
                listViewStore.attachmentViewStates.set(attachmentViewStateKey, attachmentViewState);

                // Add the view state key to the AttachmentPreviewWellView
                const shouldShowImageView = await lazyShouldShowImageView.import();
                if (shouldShowImageView(attachment)) {
                    attachmentPreviewWellView.imageViewStateIds.push(attachmentViewStateKey);
                } else {
                    attachmentPreviewWellView.documentViewStateIds.push(attachmentViewStateKey);
                }
            });
    }
);

/**
 * Process attachment previews for the conversations
 * @param conversations received in the GetAttachmentPreviews response
 * @param mailboxInfo
 */
export function processAttachmentPreviewsForConversations(
    conversations: ConversationType[],
    mailboxInfo: MailboxInfo
) {
    conversations.forEach(conversation => {
        processAttachmentsForConversation(conversation, mailboxInfo);
    });
}

/**
 * Create the attachment well view state for the conversation
 * @param conversation - conversation payload from the getAttachmentPreviews response
 */
export let processAttachmentsForConversation = select<ProcessAttachmentState>({
    listView: () => listViewStore,
})(
    action('processAttachmentsForConversation')(function processAttachmentsForConversation(
        conversation: ConversationType,
        mailboxInfo: MailboxInfo,
        state?: ProcessAttachmentState
    ) {
        const conversationItem = state.listView.conversationItems.get(
            conversation.ConversationId.Id
        );

        // Conversation may have been removed on the client, in which case we ignore the response
        if (!conversationItem) {
            return;
        }

        if (conversation.AttachmentPreviews) {
            /**
             * Step 1 - De-dupe previews for conversations
             * Most of the times the duplicates that we have seen have same file name but the size is different
             * This happens when the conversation is getting updated in different outlook clients.
             */
            const dedupedPreviews = getDedupedInlinePreviews(
                conversation.AttachmentPreviews.Previews
            );

            // Step 2 - process deduped previews for conversation
            processAttachmentsForRow(conversation.ConversationId.Id, dedupedPreviews, mailboxInfo);

            // Log number of inline images that were deduped
            const numOfInlineImagesDeduped =
                conversation.AttachmentPreviews.Previews.length - dedupedPreviews.length;

            if (numOfInlineImagesDeduped > 0) {
                logVerboseUsage(INLINEPREVIEWS_INLINEIMAGE_DUP, [numOfInlineImagesDeduped]);
            }
        } else {
            // Set this flag so that the placeholder space is removed.
            conversationItem.shouldShowAttachmentPreviews = false;
            removeAttachmentPreviewsForRow(conversation.ConversationId.Id);

            // Log everytime we did not receive any attachment previews
            logVerboseUsage(INLINEPREVIEWS_FALSEPOSITIVE);
        }
    })
);

export let processAttachmentPreviewsForItems = action('processAttachmentPreviewsForItems')(
    function processAttachmentPreviewsForItems(
        conversations: ConversationType[],
        itemIds: string[],
        mailboxInfo: MailboxInfo
    ) {
        /**
         * Step 1 - Get the conversations for which we have received previews
         * Some shall not return previews as there are false positives.
         */
        const conversationsWithPreviews = conversations.filter(conversation => {
            return conversation.AttachmentPreviews;
        });

        /**
         * Step 2 - Create a collection of all the previews received
         */
        const allAttachmentPreviews = [];
        conversationsWithPreviews.forEach(conversation => {
            allAttachmentPreviews.push(...conversation.AttachmentPreviews.Previews);
        });

        /**
         * Step 3 - Create the final list of previews that should be processed
         * Process only the previews whose parent item id is present in the itemIds list which is
         * the list used to fetch the previews for.
         */
        const previewsToProcess = {};
        allAttachmentPreviews.forEach(preview => {
            const itemId = preview.ParentItemId.Id;
            if (itemIds.indexOf(itemId) > -1) {
                if (!previewsToProcess[itemId]) {
                    previewsToProcess[itemId] = [];
                }

                previewsToProcess[itemId].push(preview);
            }
        });

        /**
         * Step 4s
         * 1. If the item is already deleted then do not process previews for it. Else
         * 2.1 If preview is not received for item do clean up, set show flag to false and clean up store. else
         * 2.2 Proceed with the processing of the previews
         */
        itemIds.forEach(itemId => {
            const clientItem = mailStore.items.get(itemId);
            if (!clientItem) {
                delete previewsToProcess[itemId];
            } else {
                if (!previewsToProcess[itemId]) {
                    // 1. Set shouldShowAttachmentPreviews flag to false so that the placeholder space is removed.
                    clientItem.shouldShowAttachmentPreviews = false;

                    // 2. Remove attachment previews from the list view store maps
                    removeAttachmentPreviewsForRow(itemId);
                } else {
                    processAttachmentsForRow(itemId, previewsToProcess[itemId], mailboxInfo);
                }
            }
        });
    }
);

/**
 * Create the attachment well view state for the row
 * @param rowId - row id
 * @param previews - previews to be stored for this row
 * @param mailboxInfo - mailbox info to which the preview belongs
 */
export let processAttachmentsForRow = action('processAttachmentsForRow')(
    async function processAttachmentsForRow(
        rowId: string,
        previews: AttachmentPreview[],
        mailboxInfo: MailboxInfo
    ) {
        const attachmentPreviewWellView = {
            documentViewStateIds: [],
            imageViewStateIds: [],
        };

        await Promise.all(
            previews.map(async preview => {
                await createAndStoreAttachmentViewState(
                    rowId,
                    {
                        ...preview.ParentItemId,
                        mailboxInfo: mailboxInfo,
                    },
                    preview.Attachment,
                    attachmentPreviewWellView,
                    mailboxInfo
                );
            })
        );

        listViewStore.rowAttachmentPreviewWellViews.set(rowId, attachmentPreviewWellView);
    }
);
